import { useEffect, useState } from "react";
import { safeFetch } from "@/api/safeFetch";
import { Input, ButtonSiguiente } from "@/components/ui";
import { UsuarioDetalle } from "@/types";
import { useNavigate } from "react-router-dom";

const timeZone = "Europe/Madrid";

function formatDate(dateLike: string | Date | null | undefined): string {
    if (!dateLike) return "-";
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "-";
    return new Intl.DateTimeFormat("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone,
    }).format(d);
}

function formatTime(dateLike: string | Date | null | undefined): string {
    if (!dateLike) return "-";
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "-";
    return new Intl.DateTimeFormat("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone,
    }).format(d);
}

/* function formatDateTime(dateLike: string | Date | null | undefined): string {
    const date = formatDate(dateLike);
    const time = formatTime(dateLike);
    return (date === "-" && time === "-") ? "-" : `${date} ${time}`;
} */

const BuscarUsuarioDetalle: React.FC = () => {
    const [q, setQ] = useState("");
    const [usuario, setUsuario] = useState<UsuarioDetalle | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem("token") ?? "";

    const buscar = async () => {
        if (!q.trim()) return;

        const controller = new AbortController();
        const signal = controller.signal;

        setLoading(true);
        setError(null);
        setUsuario(null);

        try {
            const res = await safeFetch(
                `/api/admin/usuarios/detalle?q=${encodeURIComponent(q)}`,
                { headers: { Authorization: `Bearer ${token}` }, signal }
            );

            if (!res.ok) {
                const json = await res.json().catch(() => ({}));
                throw new Error(json.message || "Usuario no encontrado");
            }

            const data: UsuarioDetalle = await res.json();
            setUsuario(data);
        } catch (err: any) {
            if (err.name !== "AbortError") {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }

        return () => controller.abort();
    };


    useEffect(() => {
        return () => {
            setQ("");
            setUsuario(null);
            setError(null);
            setLoading(false);
        };
    }, []);


    // colores etc. (tu código)
    const colores = ["bg-blue-100", "bg-green-100", "bg-yellow-100", "bg-purple-100", "bg-pink-100"];

    return (
        <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative dark:text-zinc-700">
           
                {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full max-w-3xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 shadow-purple-700 h-[calc(90vh-4rem)]">
                <h2 className="text-2xl font-bold text-center"> Detalle completo de Usuario </h2>

                {/* BUSCADOR */}
                <div className="w-full bg-purple-600 p-4 rounded-xl flex gap-4 items-center border-white border-r-4 border-t-2 border shadow-md shadow-purple-700">
                    <div className="flex-1">
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por DNI / NIE / email / ID" />
                    </div>
                    <div className="flex-1">
                        <ButtonSiguiente onClick={buscar} className="w-full h-[32px] mb-1">Buscar</ButtonSiguiente>
                    </div>
                </div>

                {loading && <p>Buscando…</p>}
                {error && <p className="text-red-600">{error}</p>}

                {/* USUARIO */}
                {usuario && (
                    <>
                        <section className="bg-white p-4 rounded shadow">
                            <h3 className="font-bold text-lg mb-2">👤 Usuario</h3>
                            <p><b>Usuario:</b> @{usuario.nombre_usuario}</p>
                            <p><b>Nombre:</b> {usuario.nombre} {usuario.apellidos}</p>
                            <p><b>Email:</b> {usuario.email}</p>
                            <p><b>DNI/NIE:</b> {usuario.dni ?? usuario.nie ?? "-"}</p>
                            <p><b>Rol:</b> {usuario.roles?.[0] ?? "-"}</p>
                            <p><b>Verificado:</b> {usuario.verificado ? "✔ Sí" : "✖ No"}</p>
                            <p>
                                <b>Creado:</b> {formatDate(usuario.creado_en)} <span className="text-sm text-zinc-500">({formatTime(usuario.creado_en)})</span>
                            </p>
                        </section>

                        {/* PROFESIONALES */}
                        <section className="space-y-6">
                            <h3 className="font-bold text-lg">🏢 Profesionales asociados ({usuario.profesionales.length})</h3>

                            {usuario.profesionales.map((p, index) => (
                                <div key={p.profesional_id} className={`${colores[index % colores.length]} p-4 rounded shadow space-y-3`}>
                                    <h2 className="text-xl text-zinc-800 font-bold text-center">{p.nombre_profesion}</h2>
                                    <p><b>Empresa:</b> {p.nombre_empresa}</p>
                                    <p><b>CIF:</b> {p.cif}</p>

                                    <p><b>Descripción</b><br />
                                        {p.descripcion}</p>

                                    {/* DIRECCIÓN */}
                                    {p.direccion && (
                                        <div>
                                            <b>📍 Dirección</b>
                                            <p><b>Calle:</b> {p.direccion.calle}.<br />
                                                <b>Localidad:</b> {p.direccion.ciudad}.<br />
                                                <b>Provincia:</b> {p.direccion.provincia}.<br />
                                                <b>C.P:</b> ({p.direccion.cp}).</p>
                                        </div>
                                    )}

                                    {/* CONTACTO */}
                                    <div>
                                        <b>☎️ Contacto</b>
                                        <p>Teléfonos: {p.telefonos.length ? p.telefonos.join(", ") : "-"}</p>
                                        <p>Emails: {p.correos.length ? p.correos.join(", ") : "-"}</p>
                                    </div>

                                    {/* SUSCRIPCIONES */}
                                    <div>
                                        <b>💳 Suscripciones</b>
                                        {p.suscripciones.length === 0 ? (
                                            <p>No tiene suscripciones</p>
                                        ) : (
                                            p.suscripciones.map((s, i) => (
                                                <div key={i} className="border p-2 rounded mt-2">
                                                    <p><b>Estado:</b> {s.estado}</p>
                                                    <p>
                                                        <b>Periodo:</b><br />
                                                        Fecha inicio → {formatDate(s.fecha_inicio)} {formatTime(s.fecha_inicio)}<br />
                                                        Fecha fin &nbsp;&nbsp;&nbsp;→ {formatDate(s.fecha_fin)} {formatTime(s.fecha_fin)}
                                                    </p>

                                                    <p><b>Monto:</b> {s.monto} {s.moneda ?? "EUR"}</p>
                                                    {/*  {s.img_contrato && <p><a className="text-blue-600" href={`${import.meta.env.VITE_BACKEND_URL}/${s.img_contrato}`} target="_blank" rel="noreferrer">Ver contrato</a></p>} */}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ))}

                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export default BuscarUsuarioDetalle;
